import { z } from 'zod';
import {
    CredentialRotationAttemptState,
    CredentialType,
    Status,
} from '@pillar-portal/database/dist/enums';
import {
    EncryptionKeyCloudVendor,
    EncryptionKeySecretManagerReferenceStorageVersion,
    EncryptionKeySecretManagerVersionNumber,
    type EncryptionKeyCredentialListItem,
    type EncryptionKeyCredentialRotationAttemptListItem,
    type EncryptionKeyListItem,
    type EncryptionKeySecretManagerReference,
    type EncryptionKeyUsageRekeyCounts,
    type EncryptionKeyUsageCounts,
    type GcpEncryptionKeySecretManagerReference,
    type ListEncryptionKeyCredentialRotationAttemptsRequest,
    type ListEncryptionKeyCredentialRotationAttemptsResponse,
    type ListEncryptionKeyCredentialsRequest,
    type ListEncryptionKeyCredentialsResponse,
    type ListEncryptionKeysRequest,
    type ListEncryptionKeysResponse,
    type RekeyEncryptionKeyUsageRequest,
    type RekeyEncryptionKeyUsageResponse,
    type RotateEncryptionKeyResponse,
} from '../encryption-key.types';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { SupportedCredentialSystemIdSchema } from './secret.schemas';
import {
    defineSafeIntegerDomain,
    normalizeStoredJsonSafeIntegerObjectFields,
} from '../stored-json-number';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../stored-json-contract.types';
import { SystemDisplayTimeZoneSchema } from '../timezone.utils';

export const EncryptionKeyPublicIdentifierSchema: z.ZodType<string> = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_-]+$/);

export const EncryptionKeyListLimitSchema: z.ZodType<number> = z.coerce
    .number()
    .int()
    .min(1)
    .max(100)
    .default(50);

export const EncryptionKeyUuidCursorSchema: z.ZodType<string | undefined> = z
    .string()
    .uuid()
    .optional();

export type GcpEncryptionKeySecretManagerReferenceV1 = {
    cloud_vendor: typeof EncryptionKeyCloudVendor.GCP;
    family: string;
    sequence_number: number;
};

export const GcpEncryptionKeySecretManagerReferenceV1Schema: z.ZodType<GcpEncryptionKeySecretManagerReferenceV1> =
    z
        .object({
            cloud_vendor: z.literal(EncryptionKeyCloudVendor.GCP),
            family: z.string().min(1),
            sequence_number: z.number().int().safe().positive(),
        })
        .strict();

export const GcpEncryptionKeySecretManagerReferenceSchema: z.ZodType<GcpEncryptionKeySecretManagerReference> =
    z
        .object({
            secret_manager_reference_version: z.literal(
                EncryptionKeySecretManagerReferenceStorageVersion.V2
            ),
            cloud_vendor: z.literal(EncryptionKeyCloudVendor.GCP),
            family: z.string().min(1),
            sequence_number: z.number().int().safe().positive(),
            version_number: z.literal(EncryptionKeySecretManagerVersionNumber.V1),
        })
        .strict();

const EncryptionKeySequenceNumberSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'encryption-key-secret-manager-reference.sequence_number',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'GcpEncryptionKeySecretManagerReferenceSchema.sequence_number',
        minimum: 1,
        maximum: Number.MAX_SAFE_INTEGER,
    },
});

export const EncryptionKeySecretManagerReferenceSchema: z.ZodType<EncryptionKeySecretManagerReference> =
    GcpEncryptionKeySecretManagerReferenceSchema;

export const StoredEncryptionKeySecretManagerReferenceV1Schema: z.ZodType<GcpEncryptionKeySecretManagerReferenceV1> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: EncryptionKeySequenceNumberSafeIntegerDomain,
                fields: ['sequence_number'],
            }),
        GcpEncryptionKeySecretManagerReferenceV1Schema
    );

const EncryptionKeySecretManagerReferenceV2MarkerSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'encryption-key-secret-manager-reference.secret_manager_reference_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: [EncryptionKeySecretManagerReferenceStorageVersion.V2],
    },
});

const EncryptionKeySecretManagerVersionNumberSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'encryption-key-secret-manager-reference.version_number',
    provenance: {
        kind: 'finite-integer-domain',
        values: [EncryptionKeySecretManagerVersionNumber.V1],
    },
});

export const StoredEncryptionKeySecretManagerReferenceV2Schema: z.ZodType<GcpEncryptionKeySecretManagerReference> =
    z.preprocess((value: unknown): unknown => {
        const normalizedVersion = normalizeStoredJsonSafeIntegerObjectFields(value, {
            safeIntegerDomain: EncryptionKeySecretManagerReferenceV2MarkerSafeIntegerDomain,
            fields: ['secret_manager_reference_version'],
        });
        const normalizedSecretVersion = normalizeStoredJsonSafeIntegerObjectFields(
            normalizedVersion,
            {
                safeIntegerDomain: EncryptionKeySecretManagerVersionNumberSafeIntegerDomain,
                fields: ['version_number'],
            }
        );
        return normalizeStoredJsonSafeIntegerObjectFields(normalizedSecretVersion, {
            safeIntegerDomain: EncryptionKeySequenceNumberSafeIntegerDomain,
            fields: ['sequence_number'],
        });
    }, EncryptionKeySecretManagerReferenceSchema);

export function normalizeEncryptionKeySecretManagerReferenceV1(
    reference: GcpEncryptionKeySecretManagerReferenceV1
): EncryptionKeySecretManagerReference {
    return {
        secret_manager_reference_version: EncryptionKeySecretManagerReferenceStorageVersion.V2,
        ...reference,
        version_number: EncryptionKeySecretManagerVersionNumber.V1,
    };
}

export function normalizeEncryptionKeySecretManagerReferenceV2(
    reference: GcpEncryptionKeySecretManagerReference
): EncryptionKeySecretManagerReference {
    return reference;
}

export const EncryptionKeySecretManagerReferenceStorageContract = defineStoredJsonContract({
    contractName: 'EncryptionKeySecretManagerReference',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredEncryptionKeySecretManagerReferenceV1Schema',
            schema: StoredEncryptionKeySecretManagerReferenceV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['secret_manager_reference_version'],
                },
            ],
            normalize: normalizeEncryptionKeySecretManagerReferenceV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredEncryptionKeySecretManagerReferenceV2Schema',
            schema: StoredEncryptionKeySecretManagerReferenceV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['secret_manager_reference_version'],
                    markerValue: EncryptionKeySecretManagerReferenceStorageVersion.V2,
                },
            ],
            normalize: normalizeEncryptionKeySecretManagerReferenceV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredEncryptionKeySecretManagerReferenceParser: z.ZodType<EncryptionKeySecretManagerReference> =
    buildStoredJsonValueParser(EncryptionKeySecretManagerReferenceStorageContract);

export const RotateEncryptionKeyResponseSchema: z.ZodType<RotateEncryptionKeyResponse> = z
    .object({
        secret_manager_reference: EncryptionKeySecretManagerReferenceSchema,
        secret_name: z.string().min(1),
        encryption_algorithm: z.literal('AES-256-GCM'),
        status: z.literal(Status.ACTIVE),
    })
    .strict();

export const RekeyEncryptionKeyUsageRequestSchema: z.ZodType<RekeyEncryptionKeyUsageRequest> = z
    .object({
        limit: EncryptionKeyListLimitSchema.optional(),
        dry_run: z.boolean().optional(),
    })
    .strict();

export const EncryptionKeyUsageRekeyCountsSchema: z.ZodType<EncryptionKeyUsageRekeyCounts> = z
    .object({
        credentials: z.number().int().nonnegative(),
        credential_rotation_attempts: z.number().int().nonnegative(),
        oauth_authorization_grants: z.number().int().nonnegative(),
        oauth_authorization_transactions: z.number().int().nonnegative(),
    })
    .strict();

export const RekeyEncryptionKeyUsageResponseSchema: z.ZodType<RekeyEncryptionKeyUsageResponse> = z
    .object({
        active_secret_name: EncryptionKeyPublicIdentifierSchema,
        active_secret_manager_reference: EncryptionKeySecretManagerReferenceSchema,
        dry_run: z.boolean(),
        scanned: EncryptionKeyUsageRekeyCountsSchema,
        rekeyed: EncryptionKeyUsageRekeyCountsSchema,
        skipped_concurrent_updates: EncryptionKeyUsageRekeyCountsSchema,
        remaining: EncryptionKeyUsageRekeyCountsSchema,
    })
    .strict();

export const EncryptionKeyUsageCountsSchema: z.ZodType<EncryptionKeyUsageCounts> = z
    .object({
        credentials: z.number().int().nonnegative(),
        credential_rotation_attempts: z.number().int().nonnegative(),
        oauth_authorization_grants: z.number().int().nonnegative(),
        oauth_authorization_transactions: z.number().int().nonnegative(),
    })
    .strict();

export const EncryptionKeyListItemSchema: z.ZodType<EncryptionKeyListItem> = z
    .object({
        secret_name: EncryptionKeyPublicIdentifierSchema,
        secret_manager_reference: EncryptionKeySecretManagerReferenceSchema,
        encryption_algorithm: z.string().min(1),
        status: z.enum(Status),
        usage_counts: EncryptionKeyUsageCountsSchema,
    })
    .strict();

export const ListEncryptionKeysRequestSchema: z.ZodType<ListEncryptionKeysRequest> = z
    .object({
        limit: EncryptionKeyListLimitSchema.optional(),
        cursor: EncryptionKeyPublicIdentifierSchema.optional(),
    })
    .strict();

export const ListEncryptionKeysResponseSchema: z.ZodType<ListEncryptionKeysResponse> = z
    .object({
        encryption_keys: z.array(EncryptionKeyListItemSchema),
        pagination: OpaqueCursorPaginationSchema,
    })
    .strict();

export const EncryptionKeyCredentialListItemSchema: z.ZodType<EncryptionKeyCredentialListItem> = z
    .object({
        credential_uuid: z.string().uuid(),
        organization_uuid: z.string().uuid(),
        system_id: SupportedCredentialSystemIdSchema,
        account_id: z.string().min(1),
        credential_type: z.enum(CredentialType),
        credential_label: z.string().nullable(),
        public_fields: z.record(z.string(), z.unknown()),
        masked_preview: z.string(),
        status: z.enum(Status),
        applies_to_children: z.boolean(),
    })
    .strict();

export const ListEncryptionKeyCredentialsRequestSchema: z.ZodType<ListEncryptionKeyCredentialsRequest> =
    z
        .object({
            limit: EncryptionKeyListLimitSchema.optional(),
            cursor: EncryptionKeyUuidCursorSchema,
        })
        .strict();

export const ListEncryptionKeyCredentialsResponseSchema: z.ZodType<ListEncryptionKeyCredentialsResponse> =
    z
        .object({
            credentials: z.array(EncryptionKeyCredentialListItemSchema),
            pagination: OpaqueCursorPaginationSchema,
        })
        .strict();

export const EncryptionKeyCredentialRotationAttemptListItemSchema: z.ZodType<EncryptionKeyCredentialRotationAttemptListItem> =
    z
        .object({
            credential_rotation_attempt_uuid: z.string().uuid(),
            credential: EncryptionKeyCredentialListItemSchema,
            organization_uuid: z.string().uuid(),
            state: z.enum(CredentialRotationAttemptState),
            flow_key: z.string().min(1),
            target_field_keys: z.array(z.string().min(1)),
            credential_definition_digest: z.string().min(1),
            ipx_web_resource_model_digest: z.string().min(1).nullable(),
            created_ts: z.string().datetime(),
            expires_ts: z.string().datetime(),
            injected_ts: z.string().datetime().nullable(),
            submitted_ts: z.string().datetime().nullable(),
            promoted_ts: z.string().datetime().nullable(),
            failed_ts: z.string().datetime().nullable(),
        })
        .strict();

export const ListEncryptionKeyCredentialRotationAttemptsRequestSchema: z.ZodType<ListEncryptionKeyCredentialRotationAttemptsRequest> =
    z
        .object({
            limit: EncryptionKeyListLimitSchema.optional(),
            cursor: EncryptionKeyUuidCursorSchema,
        })
        .strict();

export const ListEncryptionKeyCredentialRotationAttemptsResponseSchema: z.ZodType<ListEncryptionKeyCredentialRotationAttemptsResponse> =
    z
        .object({
            credential_rotation_attempts: z.array(
                EncryptionKeyCredentialRotationAttemptListItemSchema
            ),
            pagination: OpaqueCursorPaginationSchema,
            display_timezone: SystemDisplayTimeZoneSchema,
        })
        .strict();
